import "@/App.css";
import { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ShieldCheck,
  Paintbrush,
  ClipboardCheck,
  KeyRound,
  MapPin,
  Phone,
  Mail,
  ArrowRight,
  Menu,
  X,
  BadgeCheck,
  Building2,
  Handshake,
  Languages,
  Send,
  CheckCircle2,
} from "lucide-react";
import { content, PHONE_DISPLAY, PHONE_LINK, EMAIL, ADDRESS } from "@/content";

const GOLD = "#B99A5C";
const GOLD_DEEP = "#80683D";

const fadeUp = {
  initial: { opacity: 0, y: 32 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

const Overline = ({ children, light = false }) => (
  <p
    className={`text-xs sm:text-sm font-semibold uppercase tracking-[0.22em] ${
      light ? "text-[#E7D8B8]" : "text-[#B99A5C]"
    }`}
  >
    {children}
  </p>
);

const Header = ({ t }) => {
  const [open, setOpen] = useState(false);
  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-stone-200"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
        <a href="#top" data-testid="header-logo-link" aria-label="Golden Comfort Property Management — home">
          <img
            src="/assets/logo.svg"
            alt="Golden Comfort Property Management"
            className="h-20 w-auto"
            data-testid="header-logo"
          />
        </a>
        <nav className="hidden lg:flex items-center gap-10" aria-label="Primary">
          {t.nav.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={l.id}
              className="text-sm font-medium tracking-wide text-stone-600 hover:text-[#80683D] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-6">
          <Link
            to={t.langSwitchPath}
            data-testid="language-switch"
            className="flex items-center gap-1.5 text-sm font-medium text-stone-600 hover:text-[#80683D] transition-colors duration-300"
          >
            <Languages size={15} color={GOLD} />
            {t.langSwitchLabel}
          </Link>
          <a
            href={PHONE_LINK}
            data-testid="header-phone-link"
            className="flex items-center gap-2 text-sm font-semibold text-stone-800 hover:text-[#80683D] transition-colors duration-300"
          >
            <Phone size={15} color={GOLD} />
            <span dir="ltr" className="inline-block">{PHONE_DISPLAY}</span>
          </a>
          <a
            href="#contact"
            data-testid="header-get-in-touch-btn"
            className="bg-[#B99A5C] text-white text-sm font-medium tracking-wide px-7 py-3 hover:bg-[#80683D] transition-colors duration-300"
          >
            {t.getInTouch}
          </a>
        </div>
        <button
          className="lg:hidden text-stone-800"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-stone-200 px-6 py-6 flex flex-col gap-5" data-testid="mobile-menu">
          {t.nav.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              data-testid={`mobile-${l.id}`}
              className="text-base font-medium text-stone-700"
            >
              {l.label}
            </a>
          ))}
          <Link
            to={t.langSwitchPath}
            onClick={() => setOpen(false)}
            data-testid="mobile-language-switch"
            className="flex items-center gap-2 text-base font-medium text-stone-700"
          >
            <Languages size={16} color={GOLD} />
            {t.langSwitchLabel}
          </Link>
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            data-testid="mobile-get-in-touch-btn"
            className="bg-[#B99A5C] text-white text-center text-sm font-medium tracking-wide px-7 py-3"
          >
            {t.getInTouch}
          </a>
        </div>
      )}
    </header>
  );
};

const Hero = ({ t }) => (
  <section id="top" data-testid="hero-section" className="relative min-h-[92vh] flex items-center hero-grain">
    <div className="absolute inset-0">
      <img
        src="/assets/hero.jpg"
        alt={t.hero.bgAlt}
        className="w-full h-full object-cover"
        data-testid="hero-background-image"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-stone-950/85 via-stone-950/55 to-stone-950/20" />
    </div>
    <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-36 pb-24 w-full">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-2xl"
      >
        <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.24em] text-[#E7D8B8] mb-6" data-testid="hero-overline">
          {t.hero.overline}
        </p>
        <h1
          className="font-serif-display text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.12] tracking-tight text-white"
          data-testid="hero-headline"
        >
          {t.hero.headline}
        </h1>
        <p
          className="mt-7 text-base sm:text-lg font-light leading-relaxed text-stone-200 max-w-xl"
          data-testid="hero-subheadline"
        >
          {t.hero.sub}
        </p>
        <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-6">
          <a
            href="#contact"
            data-testid="hero-get-in-touch-btn"
            className="inline-flex items-center justify-center gap-3 bg-[#B99A5C] text-white font-medium tracking-wide px-9 py-4 hover:bg-[#80683D] transition-colors duration-300"
          >
            {t.getInTouch}
            <ArrowRight size={17} className="rtl:-scale-x-100" />
          </a>
          <p className="text-sm text-stone-300 flex items-center gap-2" data-testid="hero-reassurance">
            <ShieldCheck size={17} color={GOLD} className="shrink-0" />
            {t.hero.reassurance}
          </p>
        </div>
        <p className="mt-12 text-xs uppercase tracking-[0.2em] text-stone-400 flex items-center gap-2" data-testid="hero-positioning">
          <MapPin size={13} color={GOLD} className="shrink-0" />
          {t.hero.positioning}
        </p>
      </motion.div>
    </div>
  </section>
);

const TrustBar = ({ t }) => {
  const icons = [BadgeCheck, Building2, Handshake];
  return (
    <div className="bg-[#1C1917]" data-testid="trust-bar">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
        {t.trust.map((text, i) => {
          const Icon = icons[i];
          return (
            <motion.div
              key={text}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              className="flex items-center gap-3"
              data-testid={`trust-bar-item-${i + 1}`}
            >
              <Icon size={20} color={GOLD} className="shrink-0" />
              <span className="text-sm font-medium tracking-wide text-stone-200">{text}</span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

const ValueProposition = ({ t }) => {
  const icons = [ShieldCheck, Paintbrush, ClipboardCheck, KeyRound, MapPin];
  const spans = ["md:col-span-7", "md:col-span-5", "md:col-span-4", "md:col-span-4", "md:col-span-4"];
  return (
    <section id="why-us" data-testid="value-proposition-section" className="py-24 lg:py-32 bg-[#FAFAF9]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div {...fadeUp} className="max-w-2xl mb-16">
          <Overline>{t.value.overline}</Overline>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-stone-900 mt-5 gold-underline" data-testid="value-proposition-heading">
            {t.value.heading}
          </h2>
          <p className="mt-8 text-base sm:text-lg font-light leading-relaxed text-stone-600">
            {t.value.intro}
          </p>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {t.value.items.map(({ title, copy }, i) => {
            const Icon = icons[i];
            return (
              <motion.div
                key={title}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 3) * 0.1 }}
                className={`${spans[i]} group bg-white border border-stone-200 hover:-translate-y-1 hover:shadow-xl hover:shadow-stone-200 transition-[transform,box-shadow] duration-500 flex flex-col`}
                data-testid={`benefit-card-${i + 1}`}
              >
                {i === 0 && (
                  <div className="h-52 overflow-hidden">
                    <img
                      src="/assets/interior.jpg"
                      alt={t.value.imageAlt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      data-testid="benefit-card-image"
                    />
                  </div>
                )}
                <div className="p-8 lg:p-10 flex flex-col gap-4 flex-1">
                  <div className="w-11 h-11 flex items-center justify-center border border-[#E7D8B8] bg-[#E7D8B8]/20">
                    <Icon size={20} color={GOLD_DEEP} />
                  </div>
                  <h3 className="font-serif-display text-xl sm:text-2xl font-medium text-stone-900">{title}</h3>
                  <p className="text-sm sm:text-base font-light leading-relaxed text-stone-600">{copy}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const HowItWorks = ({ t }) => (
  <section id="how-it-works" data-testid="how-it-works-section" className="py-24 lg:py-32 bg-white border-y border-stone-200">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
      <motion.div {...fadeUp} className="lg:col-span-5">
        <div className="lg:sticky lg:top-32">
          <Overline>{t.process.overline}</Overline>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-stone-900 mt-5 gold-underline" data-testid="how-it-works-heading">
            {t.process.heading}
          </h2>
          <p className="mt-8 text-base sm:text-lg font-light leading-relaxed text-stone-600">
            {t.process.intro}
          </p>
          <div className="mt-10 h-72 overflow-hidden hidden lg:block">
            <img
              src="/assets/townhouse.jpg"
              alt={t.process.imageAlt}
              className="w-full h-full object-cover"
              data-testid="how-it-works-image"
            />
          </div>
        </div>
      </motion.div>
      <div className="lg:col-span-7">
        {t.process.steps.map((step, i) => (
          <motion.div
            key={step.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.55, delay: 0.05 }}
            className="step-item relative flex gap-6 pb-12 last:pb-0"
            data-testid={`process-step-${i + 1}`}
          >
            <div className="step-line relative shrink-0">
              <div className="w-14 h-14 flex items-center justify-center border border-[#B99A5C] bg-white relative z-10">
                <span className="font-serif-display text-lg font-medium text-[#80683D]">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
            </div>
            <div className="pt-2">
              <h3 className="font-serif-display text-xl sm:text-2xl font-medium text-stone-900">{step.title}</h3>
              <p className="mt-3 text-sm sm:text-base font-light leading-relaxed text-stone-600 max-w-xl">{step.copy}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Authority = ({ t }) => (
  <section id="about" data-testid="authority-section" className="py-24 lg:py-32 bg-[#FAFAF9]">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
      <motion.div {...fadeUp} className="portrait-frame max-w-md mx-auto lg:mx-0 w-full">
        <img
          src="/assets/founder.jpg"
          alt={t.authority.photoAlt}
          className="relative z-10 w-full h-auto object-cover"
          data-testid="founder-photo"
        />
      </motion.div>
      <motion.div {...fadeUp}>
        <Overline>{t.authority.overline}</Overline>
        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-stone-900 mt-5 gold-underline" data-testid="authority-heading">
          {t.authority.heading}
        </h2>
        <p className="mt-8 text-base sm:text-lg font-light leading-relaxed text-stone-600" data-testid="authority-intro">
          {t.authority.intro}
        </p>
        <div className="mt-10 border-l-2 rtl:border-l-0 rtl:border-r-2 border-[#B99A5C] pl-7 rtl:pl-0 rtl:pr-7">
          <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#80683D]" data-testid="founder-name">
            {t.authority.founderName}
          </p>
          <p className="mt-4 text-base font-light leading-relaxed text-stone-600" data-testid="founder-story">
            {t.authority.founderStory}
          </p>
        </div>
        <p className="mt-6 text-sm italic font-light text-stone-500" data-testid="founder-career-line">
          {t.authority.careerLine}
        </p>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6" data-testid="authority-credentials">
          {t.authority.credentials.map((item) => (
            <div key={item} className="flex items-start gap-3">
              <BadgeCheck size={17} color={GOLD} className="shrink-0 mt-0.5" />
              <span className="text-sm font-medium text-stone-700">{item}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
    <motion.div
      {...fadeUp}
      className="max-w-7xl mx-auto px-6 lg:px-10 mt-20 lg:mt-28"
      data-testid="authority-stats"
    >
      <div className="grid grid-cols-2 lg:grid-cols-4 lg:divide-x lg:divide-stone-200 border-t border-b border-stone-200">
        {t.authority.stats.map((stat, i) => (
          <div
            key={stat.value}
            className={`py-10 px-6 lg:px-10 ${i >= 2 ? "border-t border-stone-200 lg:border-t-0" : ""}`}
            data-testid={`authority-stat-${i + 1}`}
          >
            <p className="font-serif-display text-3xl lg:text-4xl font-medium text-[#B99A5C]">{stat.value}</p>
            <p className="mt-3 text-xs sm:text-sm font-light leading-relaxed text-stone-600">{stat.label}</p>
          </div>
        ))}
      </div>
    </motion.div>
  </section>
);

const FAQ = ({ t }) => (
  <section id="faq" data-testid="faq-section" className="py-24 lg:py-32 bg-white border-y border-stone-200">
    <div className="max-w-4xl mx-auto px-6 lg:px-10">
      <motion.div {...fadeUp} className="mb-14">
        <Overline>{t.faq.overline}</Overline>
        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-stone-900 mt-5 gold-underline" data-testid="faq-heading">
          {t.faq.heading}
        </h2>
      </motion.div>
      <motion.div {...fadeUp}>
        <Accordion type="single" collapsible data-testid="faq-accordion">
          {t.faq.items.map((item, i) => (
            <AccordionItem key={item.q} value={`faq-${i}`} className="border-b border-stone-200">
              <AccordionTrigger
                data-testid={`faq-question-${i + 1}`}
                className="font-serif-display text-lg sm:text-xl font-medium text-stone-900 text-start py-7 hover:text-[#80683D] hover:no-underline transition-colors duration-300"
              >
                {item.q}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`faq-answer-${i + 1}`}
                className="text-sm sm:text-base font-light leading-relaxed text-stone-600 pb-7 max-w-2xl"
              >
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </motion.div>
    </div>
  </section>
);

const RentersFAQ = ({ t }) => (
  <section id="renters-rights" data-testid="renters-rights-section" className="py-24 lg:py-32 bg-[#FAFAF9]">
    <div className="max-w-4xl mx-auto px-6 lg:px-10">
      <motion.div {...fadeUp} className="mb-14">
        <Overline>{t.rentersFaq.overline}</Overline>
        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-stone-900 mt-5 gold-underline" data-testid="renters-rights-heading">
          {t.rentersFaq.heading}
        </h2>
        <p className="mt-8 text-base sm:text-lg font-light leading-relaxed text-stone-600">
          {t.rentersFaq.intro}
        </p>
      </motion.div>
      <motion.div {...fadeUp}>
        <Accordion type="single" collapsible data-testid="renters-rights-accordion">
          {t.rentersFaq.items.map((item, i) => (
            <AccordionItem key={item.q} value={`rr-${i}`} className="border-b border-stone-200">
              <AccordionTrigger
                data-testid={`rr-question-${i + 1}`}
                className="font-serif-display text-lg sm:text-xl font-medium text-stone-900 text-start py-7 hover:text-[#80683D] hover:no-underline transition-colors duration-300"
              >
                {item.q}
              </AccordionTrigger>
              <AccordionContent
                data-testid={`rr-answer-${i + 1}`}
                className="text-sm sm:text-base font-light leading-relaxed text-stone-600 pb-7 max-w-2xl"
              >
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
        <p
          className="mt-10 text-xs font-light leading-relaxed text-stone-500 border-l-2 rtl:border-l-0 rtl:border-r-2 border-[#E7D8B8] pl-5 rtl:pl-0 rtl:pr-5"
          data-testid="renters-rights-disclaimer"
        >
          {t.rentersFaq.disclaimer}
        </p>
      </motion.div>
    </div>
  </section>
);

const Enquiry = ({ t }) => {
  const [form, setForm] = useState({ name: "", phone: "", email: "", property: "", message: "" });
  const [status, setStatus] = useState("idle");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Request failed");
      setStatus("success");
      setForm({ name: "", phone: "", email: "", property: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  const inputClass =
    "w-full border border-stone-300 bg-white px-4 py-3.5 text-sm text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-[#B99A5C] focus:ring-1 focus:ring-[#B99A5C] transition-colors duration-300";
  const labelClass = "block text-xs font-semibold uppercase tracking-[0.15em] text-stone-500 mb-2";

  return (
    <section id="contact" data-testid="enquiry-section" className="py-24 lg:py-32 bg-[#FAFAF9]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <motion.div {...fadeUp} className="lg:col-span-5">
          <Overline>{t.enquiry.overline}</Overline>
          <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-stone-900 mt-5 gold-underline" data-testid="enquiry-heading">
            {t.enquiry.heading}
          </h2>
          <p className="mt-8 text-base sm:text-lg font-light leading-relaxed text-stone-600">
            {t.enquiry.intro}
          </p>
          <ul className="mt-8 flex flex-col gap-4">
            {t.enquiry.points.map((point) => (
              <li key={point} className="flex items-start gap-3">
                <CheckCircle2 size={18} color={GOLD} className="shrink-0 mt-0.5" />
                <span className="text-sm sm:text-base font-light text-stone-700">{point}</span>
              </li>
            ))}
          </ul>
        </motion.div>
        <motion.div {...fadeUp} className="lg:col-span-7">
          <form
            onSubmit={handleSubmit}
            data-testid="enquiry-form"
            className="bg-white border border-stone-200 p-8 lg:p-10 grid grid-cols-1 sm:grid-cols-2 gap-6"
          >
            <div>
              <label htmlFor="enquiry-name" className={labelClass}>{t.enquiry.fields.name}</label>
              <input
                id="enquiry-name"
                data-testid="enquiry-name-input"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={inputClass}
                placeholder={t.enquiry.fields.name}
              />
            </div>
            <div>
              <label htmlFor="enquiry-phone" className={labelClass}>{t.enquiry.fields.phone}</label>
              <input
                id="enquiry-phone"
                data-testid="enquiry-phone-input"
                required
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className={inputClass}
                placeholder={t.enquiry.fields.phone}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="enquiry-email" className={labelClass}>{t.enquiry.fields.email}</label>
              <input
                id="enquiry-email"
                data-testid="enquiry-email-input"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={inputClass}
                placeholder={t.enquiry.fields.email}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="enquiry-property" className={labelClass}>{t.enquiry.fields.property}</label>
              <input
                id="enquiry-property"
                data-testid="enquiry-property-input"
                required
                value={form.property}
                onChange={(e) => setForm({ ...form, property: e.target.value })}
                className={inputClass}
                placeholder={t.enquiry.fields.property}
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="enquiry-message" className={labelClass}>{t.enquiry.fields.message}</label>
              <textarea
                id="enquiry-message"
                data-testid="enquiry-message-input"
                rows={4}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                className={`${inputClass} resize-none`}
                placeholder={t.enquiry.fields.message}
              />
            </div>
            <div className="sm:col-span-2">
              <button
                type="submit"
                data-testid="enquiry-submit-btn"
                disabled={status === "sending"}
                className="inline-flex items-center gap-3 bg-[#B99A5C] text-white font-medium tracking-wide px-9 py-4 hover:bg-[#80683D] transition-colors duration-300 disabled:opacity-60"
              >
                <Send size={16} className="rtl:-scale-x-100" />
                {status === "sending" ? t.enquiry.sending : t.enquiry.submit}
              </button>
              {status === "success" && (
                <p className="mt-5 text-sm font-medium text-[#80683D] flex items-center gap-2" data-testid="enquiry-success-message">
                  <CheckCircle2 size={16} color={GOLD_DEEP} />
                  {t.enquiry.success}
                </p>
              )}
              {status === "error" && (
                <p className="mt-5 text-sm font-medium text-red-700" data-testid="enquiry-error-message">
                  {t.enquiry.error}
                </p>
              )}
            </div>
          </form>
        </motion.div>
      </div>
    </section>
  );
};

const FinalCTA = ({ t }) => (
  <section id="call" data-testid="final-cta-section" className="relative py-24 lg:py-32 bg-[#1C1917] hero-grain">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
      <motion.div {...fadeUp} className="lg:col-span-7">
        <Overline light>{t.cta.overline}</Overline>
        <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-white mt-5 leading-[1.15]" data-testid="final-cta-heading">
          {t.cta.heading}
        </h2>
        <p className="mt-7 text-base sm:text-lg font-light leading-relaxed text-stone-300 max-w-xl" data-testid="final-cta-paragraph">
          {t.cta.paragraph}
        </p>
        <a
          href={PHONE_LINK}
          data-testid="final-cta-call-us-btn"
          className="mt-10 inline-flex items-center gap-3 bg-[#B99A5C] text-white font-medium tracking-wide px-9 py-4 hover:bg-[#80683D] transition-colors duration-300"
        >
          <Phone size={17} />
          {t.cta.button}
        </a>
      </motion.div>
      <motion.div {...fadeUp} className="lg:col-span-5">
        <div className="border border-stone-700/60 bg-white/[0.03] p-9 flex flex-col gap-8" data-testid="contact-details-card">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#B99A5C] mb-2">{t.cta.labels.phone}</p>
            <a href={PHONE_LINK} data-testid="contact-phone-link" className="text-lg font-medium text-white hover:text-[#E7D8B8] transition-colors duration-300 flex items-center gap-3">
              <Phone size={17} color={GOLD} /> <span dir="ltr" className="inline-block">{PHONE_DISPLAY}</span>
            </a>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#B99A5C] mb-2">{t.cta.labels.email}</p>
            <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="text-base font-light text-stone-200 hover:text-[#E7D8B8] transition-colors duration-300 flex items-center gap-3 break-all">
              <Mail size={17} color={GOLD} className="shrink-0" /> <span dir="ltr" className="inline-block">{EMAIL}</span>
            </a>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-[#B99A5C] mb-2">{t.cta.labels.address}</p>
            <p className="text-base font-light text-stone-200 flex items-start gap-3" data-testid="contact-address">
              <MapPin size={17} color={GOLD} className="shrink-0 mt-1" /> {ADDRESS}
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

const ComplianceStrip = ({ t }) => (
  <div className="bg-[#1C1917] border-t border-stone-800" data-testid="compliance-strip">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-12">
      <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-stone-500" data-testid="compliance-label">
        {t.compliance.label}
      </p>
      <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-10">
        {t.compliance.items.map((item, i) => (
          <div key={item} className="flex items-center gap-2.5" data-testid={`compliance-item-${i + 1}`}>
            <ShieldCheck size={16} color={GOLD} className="shrink-0" />
            <span className="text-xs sm:text-sm font-medium text-stone-300">{item}</span>
          </div>
        ))}
      </div>
    </div>
  </div>
);

const Footer = ({ t }) => (
  <footer className="bg-[#141211] border-t border-stone-800" data-testid="site-footer">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 flex flex-col md:flex-row items-center justify-between gap-8">
      <div className="text-center md:text-start">
        <p className="font-serif-display text-xl tracking-wide text-[#B99A5C]" data-testid="footer-brand">
          {t.footer.brand}
        </p>
        <p className="text-[11px] uppercase tracking-[0.3em] text-stone-500 mt-1">{t.footer.tagline}</p>
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-8 text-sm text-stone-400">
        <a href={PHONE_LINK} data-testid="footer-phone-link" className="hover:text-[#E7D8B8] transition-colors duration-300"><span dir="ltr" className="inline-block">{PHONE_DISPLAY}</span></a>
        <a href={`mailto:${EMAIL}`} data-testid="footer-email-link" className="hover:text-[#E7D8B8] transition-colors duration-300 break-all"><span dir="ltr" className="inline-block">{EMAIL}</span></a>
      </div>
    </div>
    <div className="border-t border-stone-800/70">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-xs text-stone-500" data-testid="footer-address">{ADDRESS}</p>
        <p className="text-xs text-stone-500" data-testid="footer-copyright">
          © {new Date().getFullYear()} {t.footer.company} {t.footer.rights}
        </p>
      </div>
    </div>
  </footer>
);

const WhatsAppButton = () => (
  <a
    href="https://wa.me/447346815326"
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Chat with Golden Comfort Property Management on WhatsApp"
    data-testid="whatsapp-button"
    className="fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-[#25D366] flex items-center justify-center shadow-lg shadow-stone-900/25 hover:scale-110 transition-transform duration-300"
  >
    <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  </a>
);

const Landing = ({ lang }) => {
  const t = content[lang];
  const rtl = lang === "ar";
  useEffect(() => {
    document.documentElement.dir = rtl ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    document.title = t.metaTitle;
  }, [lang, rtl, t]);
  return (
    <div className={`bg-[#FAFAF9] ${rtl ? "ar" : ""}`}>
      <Header t={t} />
      <main>
        <Hero t={t} />
        <TrustBar t={t} />
        <ValueProposition t={t} />
        <HowItWorks t={t} />
        <Authority t={t} />
        <FAQ t={t} />
        <RentersFAQ t={t} />
        <Enquiry t={t} />
        <FinalCTA t={t} />
        <ComplianceStrip t={t} />
      </main>
      <Footer t={t} />
      <WhatsAppButton />
    </div>
  );
};

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing lang="en" />} />
        <Route path="/ar" element={<Landing lang="ar" />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
